/**
 * The parts of a protocol or cipher suite with their own quantum view, beside the row's rating: the row is rated by
 * its weakest part (usually the key exchange), while the encryption and hash inside it may stay quantum-safe.
 */
import { cryptoParts, PART_LABEL, type PartTone } from '../lib/crypto-parts';

const TONE: Record<PartTone, string> = {
  safe: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  acceptable: 'border-amber-200 bg-amber-50 text-amber-800',
  deprecated: 'border-amber-200 bg-amber-50 text-amber-800',
  vulnerable: 'border-red-200 bg-red-50 text-red-800',
  insecure: 'border-red-300 bg-red-100 text-red-900',
  unknown: 'border-ink-200 text-ink-600',
};

export function CryptoPartsCell({ row }: { row: Record<string, unknown> }) {
  const parts = cryptoParts(row);
  if (!parts.length) {
    return <span className="text-xs text-ink-400" title="The scan recorded the protocol version without its key exchange or ciphers. Without a post-quantum hybrid (such as X25519MLKEM768) the key exchange is classical.">Parts not recorded</span>;
  }
  return (
    <span className="flex flex-wrap gap-1" data-testid="crypto-parts">
      {parts.map((p) => (
        <span key={`${p.role}|${p.name}`} title={`${p.role}: ${PART_LABEL[p.tone]}`} className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${TONE[p.tone]}`}>
          {p.name}: {PART_LABEL[p.tone]}
        </span>
      ))}
    </span>
  );
}
